// Push Notification Initializer Component
// This component handles the initialization of push notifications
// Location: src/common/components/PushNotificationInitializer.jsx

import { useEffect, useState, useRef } from 'react';
import {
  initializePushNotifications,
  setupForegroundMessageListener,
  hasRequestedPermission,
  areNotificationsEnabled,
  isNotificationPermissionDenied,
  triggerNotificationPermission,
  ensureServiceWorkerRegistered,
  getFCMToken
} from '../utils/push-notification-utils';
import { useInfoStore } from '../../App';
import useUserStore from '../../zustand/fcmToken';
import Swal from 'sweetalert2';

const PushNotificationInitializer = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const hasShownPopupRef = useRef(false);
  const isLoggedInRef = useRef(false);
  
  // Check if user is logged in using Zustand store
  const userInfo = useInfoStore((state) => state.userInfo);
  const setFcmToken = useUserStore((state) => state.setFcmToken);
  
  const isLoggedIn = userInfo && userInfo.id && userInfo.id !== null && userInfo.id !== undefined;

  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  useEffect(() => {
    // Don't initialize if user is not logged in
    if (!isLoggedIn) {
      setIsInitialized(false);
      hasShownPopupRef.current = false;
      return;
    }

    let timeoutId = null;

    const initNotifications = async () => {
      try {
        // Ensure service worker is registered
        await ensureServiceWorkerRegistered();
        
        // If notifications are already enabled, just setup listener
        if (areNotificationsEnabled()) {
          setupForegroundMessageListener();
          const token = await getFCMToken();
          if (token) {
            setFcmToken(token);
          }
          setIsInitialized(true);
          return;
        }

        // If permission was denied, show instructions
        if (isNotificationPermissionDenied()) {
          if (!hasShownPopupRef.current) {
            hasShownPopupRef.current = true;
            timeoutId = setTimeout(() => {
              if (!isLoggedInRef.current) {
                setIsInitialized(true);
                return;
              }

              Swal.fire({
                title: 'Notifications Disabled',
                html: `
                  <p>Notification permission was previously denied. To enable notifications:</p>
                  <ol style="text-align: left; margin: 20px 0;">
                    <li>Click the <strong>lock icon</strong> (🔒) in your browser's address bar</li>
                    <li>Find <strong>Notifications</strong> in the site settings</li>
                    <li>Change it to <strong>Allow</strong></li>
                    <li>Refresh this page</li>
                  </ol>
                  <p><small>Or go to your browser settings → Site permissions → Notifications</small></p>
                `,
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Try Again',
                cancelButtonText: 'Got it',
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#6c757d',
                width: '500px'
              }).then(async (result) => {
                if (result.isConfirmed) {
                  const permissionResult = await triggerNotificationPermission();
                  
                  if (permissionResult.success && permissionResult.permission === 'granted') {
                    const token = await initializePushNotifications();
                    if (token) {
                      setFcmToken(token);
                      Swal.fire({
                        title: 'Notifications Enabled!',
                        text: 'You will now receive notifications even when the website is closed.',
                        icon: 'success',
                        timer: 3000,
                        showConfirmButton: false,
                      });
                      setupForegroundMessageListener();
                    }
                  } else {
                    Swal.fire({
                      title: 'Still Disabled',
                      text: permissionResult.message,
                      icon: 'warning',
                      timer: 4000,
                      showConfirmButton: true,
                    });
                  }
                }
              });
              setIsInitialized(true);
            }, 2000);
          } else {
            setIsInitialized(true);
          }
          return;
        }

        // If permission was requested before, initialize without popup
        if (hasRequestedPermission()) {
          const token = await initializePushNotifications();
          if (token) {
            setFcmToken(token);
          }
          setupForegroundMessageListener();
          setIsInitialized(true);
          return;
        }

        // Don't show popup again if already shown
        if (hasShownPopupRef.current) {
          setIsInitialized(true);
          return;
        }

        // Show permission request popup after delay
        timeoutId = setTimeout(async () => {
          if (!isLoggedInRef.current) {
            setIsInitialized(true);
            return;
          }

          hasShownPopupRef.current = true;

          const permissionResult = await triggerNotificationPermission();
          
          if (permissionResult.success && permissionResult.permission === 'granted') {
            const token = await initializePushNotifications();
            
            if (token) {
              setFcmToken(token);
              Swal.fire({
                title: 'Notifications Enabled!',
                text: 'You will now receive notifications even when the website is closed.',
                icon: 'success',
                timer: 3000,
                showConfirmButton: false,
              });
              
              setupForegroundMessageListener();
            }
          } else {
            if (permissionResult.permission === 'denied') {
              localStorage.setItem('notificationPermissionRequested', 'true');
            } else {
              localStorage.setItem('notificationPermissionRequested', 'true');
            }
          }
          
          setIsInitialized(true);
        }, 2000);
      } catch (error) {
        console.error('❌ Error initializing push notifications:', error);
        setIsInitialized(true);
      }
    };

    // Check browser support
    const isSecureContext = 
      window.isSecureContext || 
      window.location.protocol === 'https:' || 
      window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1';
    
    const userAgent = navigator.userAgent || '';
    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
    const isFirefox = /firefox/i.test(userAgent);
    const isChrome = /chrome/i.test(userAgent) && !/edge/i.test(userAgent);
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isDesktop = !isMobile;
    
    // Safari desktop has limited support
    if (isDesktop && isSafari && !isMobile) {
      console.warn('⚠️ Safari desktop has limited push notification support. Notifications may not work properly.');
    }
    
    // Initialize if all conditions are met
    if (isSecureContext && 'serviceWorker' in navigator && 'Notification' in window && isLoggedIn && !isInitialized) {
      initNotifications();
    } else {
      if (isDesktop && isLoggedIn && !isInitialized) {
        if (!isSecureContext) {
          console.warn('⚠️ Notifications not initialized: Not in secure context (HTTPS required)');
        } else if (!('serviceWorker' in navigator)) {
          console.warn('⚠️ Notifications not initialized: Service Worker not supported');
        } else if (!('Notification' in window)) {
          console.warn('⚠️ Notifications not initialized: Notification API not supported');
        }
      }
      
      if (!isInitialized) {
        setIsInitialized(true);
      }
    }

    // Cleanup timeout on unmount
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoggedIn, isInitialized, setFcmToken]);

  return null;
};

export default PushNotificationInitializer;

